import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type { ModuleTypeView } from "../../shared/types/api";
import {
  Button,
  DataTable,
  Drawer,
  Field,
  InlineAlert,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  type Column,
} from "../../shared/ui";

/**
 * The kinds of module this organisation has.
 *
 * <p>Configuration rather than a fixed list, for the reason environments are configuration: the
 * set is not ours to decide. One organisation deploys vendor software it did not build; another
 * versions its databases alongside its services; a third keeps build-time libraries here so their
 * versions are on the record even though nothing deploys them. Under a fixed list every one of
 * those is a change to this application.
 *
 * <p>Only one setting does anything, and it is worth being blunt about which: whether a module of
 * this type has one version shared across every project, or a version per project. Everything else
 * is a name.
 */
export function ModuleTypesPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ModuleTypeView | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const types = useQuery({
    queryKey: ["module-types", "all"],
    queryFn: () => api.get<ModuleTypeView[]>("/api/v1/module-types/all"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["module-types"] });
    void queryClient.invalidateQueries({ queryKey: ["modules"] });
  };

  const setOffered = async (type: ModuleTypeView, offered: boolean) => {
    setBusy(type.id);
    setError(null);
    try {
      await api.post(`/api/v1/module-types/${type.id}/${offered ? "reinstate" : "withdraw"}`);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<ModuleTypeView>[] = [
    {
      key: "name",
      header: "Type",
      render: (type) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{type.name}</p>
          <p className="font-mono text-xs text-ink-400">{type.code}</p>
        </div>
      ),
    },
    {
      key: "sharedVersion",
      header: "Version is",
      render: (type) => (
        <StatusBadge tone={type.sharedVersion ? "violet" : "gray"}>
          {type.sharedVersion ? "Shared across projects" : "Per project"}
        </StatusBadge>
      ),
    },
    {
      key: "description",
      header: "Description",
      render: (type) => type.description ?? <span className="text-ink-400">—</span>,
    },
    {
      key: "displayOrder",
      header: "Order",
      render: (type) => <span className="tnum">{type.displayOrder}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (type) => (
        <StatusBadge tone={type.active ? "green" : "gray"}>
          {type.active ? "Offered" : "Withdrawn"}
        </StatusBadge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (type) =>
        can("MODULE_MANAGE") ? (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              loading={busy === type.id}
              onClick={(event) => {
                event.stopPropagation();
                void setOffered(type, !type.active);
              }}
            >
              {type.active ? "Withdraw" : "Offer again"}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Module types"
        subtitle="The kinds of module your estate has. The only thing a type decides is whether its version belongs to the software — shared everywhere it is used — or to one project."
        actions={
          can("MODULE_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
              New type
            </Button>
          )
        }
      />

      {error && (
        <div className="mb-3">
          <InlineAlert tone="error">{error}</InlineAlert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={types.data ?? []}
        rowKey={(type) => type.id}
        loading={types.isPending}
        onRowClick={can("MODULE_MANAGE") ? setEditing : undefined}
        emptyTitle="No module types"
        emptyMessage="Standalone and Customisable come as standard. Add your own for vendor software, libraries, databases — whatever your estate actually contains."
      />

      <ModuleTypeDrawer
        type={editing}
        open={adding || editing != null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={refresh}
      />
    </>
  );
}

function ModuleTypeDrawer({
  type,
  open,
  onClose,
  onSaved,
}: {
  type: ModuleTypeView | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = type != null;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sharedVersion, setSharedVersion] = useState(false);
  const [displayOrder, setDisplayOrder] = useState("50");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setCode(type?.code ?? "");
    setName(type?.name ?? "");
    setDescription(type?.description ?? "");
    setSharedVersion(type?.sharedVersion ?? false);
    setDisplayOrder(String(type?.displayOrder ?? 50));
    setError(null);
  }, [open, type]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const body = {
      name: name.trim(),
      description: description.trim() === "" ? null : description.trim(),
      sharedVersion,
      displayOrder: Number(displayOrder) || 0,
    };
    try {
      if (editing) {
        await api.patch(`/api/v1/module-types/${type.id}`, {
          ...body,
          lockVersion: type.lockVersion,
        });
      } else {
        await api.post("/api/v1/module-types", { ...body, code: code.trim() });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not save that type.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit module type" : "New module type"}
      subtitle="A kind of module, and how its version behaves."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={name.trim() === "" || (!editing && code.trim() === "")}
            loading={saving}
          >
            {saving ? "Saving…" : editing ? "Save" : "Create type"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!editing && (
          <Field label="Code" required hint="Short and stable — it is not editable afterwards.">
            <Input
              autoFocus
              value={code}
              placeholder="THIRD_PARTY"
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
        )}

        <Field label="Name" required>
          <Input
            value={name}
            placeholder="Third-party"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Description"
          hint="Shown when somebody picks this type for a module. Worth writing — a name alone does not say what the type means."
        >
          <Textarea
            rows={2}
            value={description}
            placeholder="Bought in, deployed by us, versioned by them."
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        {/* The only setting that changes behaviour, so it gets a full explanation rather than a
            bare label. Getting it wrong means a shared component silently stops being tracked
            across the projects that use it. */}
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-ink-300"
            checked={sharedVersion}
            onChange={(event) => setSharedVersion(event.target.checked)}
          />
          <span>
            <span className="block text-sm font-medium text-ink-900">
              One version, shared across projects
            </span>
            <span className="block text-xs text-ink-600">
              Releasing a module of this type records a new version of the software itself, and
              every project holding it can be compared against that — so a customer who has not
              taken it reads as behind. Leave unticked when the version belongs to one project and
              there is nothing shared to be behind.
            </span>
          </span>
        </label>

        <Field label="Order" hint="Where it sits in the list. Lower comes first.">
          <Input
            type="number"
            value={displayOrder}
            onChange={(event) => setDisplayOrder(event.target.value)}
          />
        </Field>

        {editing && (
          <InlineAlert tone="info">
            Changing this applies to what happens next. Versions already released keep exactly what
            they recorded — nothing about last month's release changes because the type did.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
